// AuthContext.js
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { doc, onSnapshot, getDocFromServer } from 'firebase/firestore';
import { mainApp, mainAuth, platformApp, platformAuth, db } from '../services/firebase';
import {
  setDocsSessionCookie,
  clearDocsSessionCookie,
  docsCookieRefreshDelay,
  consumeDocsReturnPath,
  forgetDocsReturnPath,
  docsCookieAccepted,
} from '../services/docs';

// Exact Site origins allowed to deliver the Site->Decode token bridge via
// postMessage. MessageEvent.origin identifies the sending Site window, not
// this Decode window that was supplied as postMessage's target origin.
const ALLOWED_BRIDGE_ORIGINS =
  process.env.REACT_APP_PLATFORM_FIREBASE_PROJECT_ID === 'staging-insurance'
    ? ['https://staging.bimanalytics.ai', 'http://localhost:3001']
    : ['https://bimanalytics.ai'];

// How long a tab opened by the Site waits for the token bridge before giving
// up with an error. The Site allows itself 20 s to mint the tokens and then
// re-posts them for up to 120 s, so anything still missing after this is not
// coming. The wait never falls back to a session Firebase restored from a
// previous visit: that session may belong to someone else on this machine.
const BRIDGE_WAIT_CAP_MS = 150000;
const DELIBERATE_SIGN_OUT = 'decode.deliberately_signed_out';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  // A surviving Site opener is not a new sign-in request after Logout. Keep
  // this tab signed out across reloads until another accepted handover begins.
  const [signedOutOnLoad] = useState(() => sessionStorage.getItem(DELIBERATE_SIGN_OUT) === '1');
  const [currentUserMain, setCurrentUserMain] = useState(null);
  const [currentUserPlatform, setCurrentUserPlatform] = useState(null);
  const [mainAuthInitialised, setMainAuthInitialised] = useState(signedOutOnLoad);
  const [platformAuthInitialised, setPlatformAuthInitialised] = useState(signedOutOnLoad);
  const [bridgeSigningIn, setBridgeSigningIn] = useState(false);
  // One slot per source of failure (bridge sign-in, main listener, Decode
  // listener, logout), so a later success from that source clears only its own
  // error and a recovered session is not left behind a stale fatal screen.
  const [authErrors, setAuthErrors] = useState({});
  const setAuthErrorFor = useCallback((source, message) => setAuthErrors((prev) => (
    prev[source] === message ? prev : { ...prev, [source]: message }
  )), []);
  const authError = authErrors.bridge || authErrors.main || authErrors.platform || authErrors.logout || null;
  const [tokenOrganisation, setTokenOrganisation] = useState(null);
  // The caller's role claim, read from the PLATFORM token. `db` is the platform
  // project's Firestore, so the platform token is the one its security rules
  // evaluate; reading the claim from mainAuth could disagree with the rule and
  // present an editable control that always fails to save.
  const [tokenRole, setTokenRole] = useState(null);
  const [emulatedOrganisation, setEmulatedOrganisation] = useState(
    () => sessionStorage.getItem('emulatedOrganisation')
  );
  // Whether a backup restore holds the workspace lock. Starts 'unknown' and a
  // failed listener lands on 'error': both keep the workspace gated (fail
  // closed) — the app never renders against data a restore may be replacing.
  // Stored with the company it was read for, so switching organisation reads as
  // 'unknown' from the very first render rather than one frame later.
  const [restoreState, setRestoreState] = useState({ company: null, userId: null, status: 'unknown' }); // status: 'unknown' | 'active' | 'inactive' | 'error'
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  // True while this tab was opened by another window (the Site's token bridge)
  // and no bridge sign-in has completed in this tab yet. Firebase restores the
  // previous session from IndexedDB before the tokens arrive; that session is
  // never rendered here, so PrivateRoute holds the sign-in screen while this
  // is set. Both flags live in sessionStorage so they survive a reload: a
  // reloaded tab keeps expecting the bridge (a completed sign-in is
  // remembered, and one that never arrived still must not fall back to the
  // restored session even if the opener has since been closed).
  const [awaitingBridge, setAwaitingBridge] = useState(() => {
    if (signedOutOnLoad) return false;
    if (window.opener) sessionStorage.setItem('bridgeExpected', '1');
    const openedBySite = sessionStorage.getItem('bridgeExpected') === '1';
    return openedBySite && !sessionStorage.getItem('bridgeSignInComplete');
  });
  const bridgeAttemptInProgressRef = useRef(false);
  const bridgePendingRef = useRef(awaitingBridge);
  const signedOutRef = useRef(signedOutOnLoad);
  const bridgeWaitTimerRef = useRef(null);
  // Invalidate token reads and documentation probes when a handover or logout
  // replaces the session, even if Firebase has not finished signing out yet.
  const authGenerationRef = useRef(0);
  const docsProbeRef = useRef(0);
  // Tracks the previous restore-lock state so we can detect the active->inactive edge.
  const wasRestoringRef = useRef({ company: null, userId: null, active: false });
  // Pending forced refresh of the Site token that keeps the docs cookie fresh.
  const docsRefreshTimerRef = useRef(null);
  // A docs hand-off that could not complete: serve_docs bounced a page request
  // here, the cookie was written, and serve_docs still could not see it
  // ('blocked', the browser is refusing the cookie) or could not be asked
  // ('unreachable'). { returnPath, status } or null. DocsHandoffSnackbar
  // shows it with retry and dismiss controls.
  const [docsHandoff, setDocsHandoff] = useState(null);

  const probeDocsHandoff = useCallback(async (returnPath, user, generation) => {
    const probe = ++docsProbeRef.current;
    let accepted = false;
    let status = 'blocked';
    try {
      accepted = await docsCookieAccepted();
    } catch (error) {
      console.error('Could not reach the documentation:', error);
      status = 'unreachable';
    }
    if (probe !== docsProbeRef.current || generation !== authGenerationRef.current
        || signedOutRef.current || bridgePendingRef.current || mainAuth.currentUser !== user) return;
    if (accepted) {
      window.location.replace(returnPath);
    } else {
      setDocsHandoff({ returnPath, status });
    }
  }, []);

  // Signs out of both Firebase projects and only then clears the derived state.
  // A rejected sign-out is not silent: the failure is shown on the sign-in gate
  // (and rethrown), since a half-signed-out tab would otherwise read as a
  // finished logout while a credential remains.
  const logout = useCallback(async ({ deliberate = true } = {}) => {
    signedOutRef.current = true;
    if (deliberate) {
      sessionStorage.setItem(DELIBERATE_SIGN_OUT, '1');
      sessionStorage.removeItem('bridgeExpected');
      bridgePendingRef.current = false;
      clearTimeout(bridgeWaitTimerRef.current);
      setAwaitingBridge(false);
    }
    authGenerationRef.current += 1;
    docsProbeRef.current += 1;
    clearTimeout(docsRefreshTimerRef.current);
    clearDocsSessionCookie();
    setAuthErrorFor('logout', null);
    const results = await Promise.allSettled([signOut(mainAuth), signOut(platformAuth)]);
    clearTimeout(docsRefreshTimerRef.current);
    clearDocsSessionCookie();
    setCurrentUserMain(null);
    setCurrentUserPlatform(null);
    setMainAuthInitialised(true);
    setPlatformAuthInitialised(true);
    setDocsHandoff(null);
    sessionStorage.removeItem('bridgeSignInComplete');
    setTokenOrganisation(null);
    setTokenRole(null);
    setEmulatedOrganisation(null);
    sessionStorage.removeItem('emulatedOrganisation');
    const failures = results.filter((result) => result.status === 'rejected');
    if (failures.length === 0) return;
    failures.forEach((failure) => console.error('Error signing out:', failure.reason));
    setAuthErrorFor('logout', failures.length === results.length
      ? 'Decode could not sign you out. Please reload the page and try again.'
      : 'Decode signed you out of one service but not the other. Please reload the page and try again, or close this tab.');
    throw failures[0].reason;
  }, [setAuthErrorFor]);

  // Determine the effective company name
  const companyName = tokenOrganisation === 'all' 
    ? emulatedOrganisation 
    : tokenOrganisation;

  useEffect(() => {
    let disposed = false;

    const receiveMessage = async (event) => {
      // Only accept the token bridge from an allow-listed Site origin, and only
      // from the window that opened us. Without these checks any page that can
      // obtain a handle to this window could inject its own custom tokens and
      // sign the user into an attacker-controlled account (session fixation).
      if (!ALLOWED_BRIDGE_ORIGINS.includes(event.origin)) return;
      if (window.opener && event.source !== window.opener) return;

      const { mainCustomToken, platformCustomToken } = event.data || {};
      if (!mainCustomToken || !platformCustomToken) return;

      // The Site re-posts the tokens until it hears back. Deliveries that land
      // while an attempt is running are simply ignored; the attempt answers
      // with an acknowledgement on success or a failure message otherwise.
      if (bridgeAttemptInProgressRef.current) return;
      const siteWindow = event.source;
      const siteOrigin = event.origin;

      sessionStorage.removeItem(DELIBERATE_SIGN_OUT);
      sessionStorage.setItem('bridgeExpected', '1');
      sessionStorage.removeItem('bridgeSignInComplete');
      bridgeAttemptInProgressRef.current = true;
      bridgePendingRef.current = true;
      signedOutRef.current = false;
      authGenerationRef.current += 1;
      docsProbeRef.current += 1;
      clearTimeout(docsRefreshTimerRef.current);
      clearDocsSessionCookie();
      setDocsHandoff(null);
      clearTimeout(bridgeWaitTimerRef.current);
      setBridgeSigningIn(true);
      setAuthErrorFor('bridge', null);
      try {
        await signInWithCustomToken(mainAuth, mainCustomToken);
        await signInWithCustomToken(platformAuth, platformCustomToken);

        // Force token refresh so custom claims (organisation, role) are
        // immediately available in Firestore/Storage security rules
        await mainAuth.currentUser.getIdToken(true);
        await platformAuth.currentUser.getIdToken(true);

        if (mainAuth.currentUser.uid !== platformAuth.currentUser.uid) {
          throw new Error('The two sign-ins refer to different users.');
        }
        bridgePendingRef.current = false;
        setAuthErrorFor('logout', null);
        sessionStorage.setItem('bridgeSignInComplete', '1');
        // Token events during the handover deliberately did not publish a
        // docs cookie. Resolve the admitted user's cookie now.
        resolveMainUser(mainAuth.currentUser);
        // Firebase does not emit onAuthStateChanged when the UID is unchanged.
        // Resolve both accepted identities even if an older token read was
        // invalidated by this handover's generation.
        resolvePlatformUser(platformAuth.currentUser);
        window.removeEventListener('message', receiveMessage);
        if (siteWindow) {
          siteWindow.postMessage({ type: 'decode-bridge-ack' }, siteOrigin);
        }
      } catch (error) {
        console.error('Error signing in with custom tokens:', error);
        console.log("Main app: ", mainApp.options.projectId);
        console.log("Platform app: ", platformApp.options.projectId);
        let rollbackFailed = false;
        try {
          await logout({ deliberate: false });
        } catch (rollbackError) {
          rollbackFailed = true;
          console.error('Error rolling back partial token bridge sign-in:', rollbackError);
        }
        setAuthErrorFor('bridge',
          'Decode could not complete sign-in. Please return to BIM Analytics and open Decode again.'
          + (rollbackFailed ? ' The stored sign-in could not be fully cleared.' : '')
        );
        if (siteWindow) {
          siteWindow.postMessage({ type: 'decode-bridge-failed' }, siteOrigin);
        }
      } finally {
        bridgeAttemptInProgressRef.current = false;
        setBridgeSigningIn(false);
        setAwaitingBridge(false);
      }
    };

    window.addEventListener('message', receiveMessage, false);

    // A tab the Site opened shows the previous visit's session to nobody. If
    // the tokens never arrive, say so; the Site closes this tab itself when
    // it could not mint them.
    if (sessionStorage.getItem(DELIBERATE_SIGN_OUT) !== '1'
        && sessionStorage.getItem('bridgeExpected') === '1'
        && !sessionStorage.getItem('bridgeSignInComplete')) {
      clearDocsSessionCookie();
      bridgeWaitTimerRef.current = setTimeout(async () => {
        window.removeEventListener('message', receiveMessage);
        let logoutFailed = false;
        try {
          await logout({ deliberate: false });
        } catch (error) {
          logoutFailed = true;
          console.error('Could not fully clear the failed sign-in handover:', error);
        }
        if (disposed) return;
        setAuthErrorFor('bridge',
          'Decode did not receive your sign-in from BIM Analytics. Close this tab, return to BIM Analytics and open Decode again.'
          + (logoutFailed ? ' The stored sign-in could not be fully cleared.' : '')
        );
        setAwaitingBridge(false);
      }, BRIDGE_WAIT_CAP_MS);
    }

    // onIdTokenChanged so the docs cookie follows every token refresh, not
    // just sign-in/out. On a refresh the state setters receive unchanged values,
    // so there is no extra rendering.
    //
    // Both listeners re-check the current user after every await. A restored
    // session's token usually needs a network refresh and resolves slowly,
    // while the bridge's fresh token resolves at once, so without the check a
    // previous visit's organisation or role could overwrite the new user's.
    async function resolveMainUser(user) {
      if (disposed || (user && signedOutRef.current)) return;
      const generation = authGenerationRef.current;
      setCurrentUserMain(user || null);
      try {
        if (user) {
          const idTokenResult = await user.getIdTokenResult();
          if (disposed || generation !== authGenerationRef.current || mainAuth.currentUser !== user) return;
          setTokenOrganisation(idTokenResult.claims.organisation);
          setAuthErrorFor('main', null);

          if (bridgePendingRef.current || signedOutRef.current) return;
          // The docs site (served under /docs by serve_docs) authenticates with
          // this same token, delivered as the __session cookie.
          setDocsSessionCookie(idTokenResult.token, idTokenResult.expirationTime);
          clearTimeout(docsRefreshTimerRef.current);
          docsRefreshTimerRef.current = setTimeout(() => {
            if (!signedOutRef.current && !bridgePendingRef.current && mainAuth.currentUser === user) {
              user.getIdToken(true).catch((err) => console.error('Docs cookie refresh failed:', err));
            }
          }, docsCookieRefreshDelay(idTokenResult.expirationTime));

          const returnPath = consumeDocsReturnPath();
          if (returnPath) await probeDocsHandoff(returnPath, user, generation);
        } else {
          setTokenOrganisation(null);
          sessionStorage.removeItem('emulatedOrganisation');
          setEmulatedOrganisation(null);
          clearTimeout(docsRefreshTimerRef.current);
          clearDocsSessionCookie();
        }
      } catch (error) {
        console.error('Error resolving main authentication state:', error);
        if (disposed || generation !== authGenerationRef.current || mainAuth.currentUser !== user) return;
        setTokenOrganisation(null);
        clearTimeout(docsRefreshTimerRef.current);
        clearDocsSessionCookie();
        setAuthErrorFor('main',
          'Decode could not verify your sign-in. Please return to BIM Analytics and open Decode again.'
        );
      } finally {
        if (!disposed && generation === authGenerationRef.current) setMainAuthInitialised(true);
      }
    }

    const unsubscribeMain = mainAuth.onIdTokenChanged(
      resolveMainUser,
      (error) => {
        console.error('Main authentication listener failed:', error);
        setCurrentUserMain(null);
        setTokenOrganisation(null);
        setMainAuthInitialised(true);
        setAuthErrorFor('main',
          'Decode could not check your sign-in. Please return to BIM Analytics and open Decode again.'
        );
      }
    );

    async function resolvePlatformUser(user) {
      if (disposed || (user && signedOutRef.current)) return;
      const generation = authGenerationRef.current;
      setCurrentUserPlatform(user || null);
      try {
        if (user) {
          const idTokenResult = await user.getIdTokenResult();
          if (disposed || generation !== authGenerationRef.current || platformAuth.currentUser !== user) return;
          setTokenRole(idTokenResult.claims.role || null);
          setAuthErrorFor('platform', null);
        } else {
          setTokenRole(null);
        }
      } catch (error) {
        console.error('Error resolving Decode role claim:', error);
        if (disposed || generation !== authGenerationRef.current || platformAuth.currentUser !== user) return;
        setTokenRole(null);
      } finally {
        if (!disposed && generation === authGenerationRef.current) setPlatformAuthInitialised(true);
      }
    }

    const unsubscribePlatform = platformAuth.onAuthStateChanged(
      resolvePlatformUser,
      (error) => {
        console.error('Decode authentication listener failed:', error);
        setCurrentUserPlatform(null);
        setTokenRole(null);
        setPlatformAuthInitialised(true);
        setAuthErrorFor('platform',
          'Decode could not check your sign-in. Please return to BIM Analytics and open Decode again.'
        );
      }
    );

    return () => {
      disposed = true;
      authGenerationRef.current += 1;
      docsProbeRef.current += 1;
      unsubscribeMain();
      unsubscribePlatform();
      window.removeEventListener('message', receiveMessage);
      clearTimeout(bridgeWaitTimerRef.current);
      clearTimeout(docsRefreshTimerRef.current);
    };
  }, [logout, probeDocsHandoff, setAuthErrorFor]);

  // Persist emulated organisation in sessionStorage
  useEffect(() => {
    if (emulatedOrganisation) {
      sessionStorage.setItem('emulatedOrganisation', emulatedOrganisation);
    } else {
      sessionStorage.removeItem('emulatedOrganisation');
    }
  }, [emulatedOrganisation]);

  const authInitialised = mainAuthInitialised && platformAuthInitialised;
  const restoreUserId = currentUserMain?.uid || null;
  const authReady = Boolean(
    authInitialised && restoreUserId && currentUserPlatform?.uid === restoreUserId
    && !awaitingBridge && !bridgeSigningIn && !authError
  );

  // Subscribe to the company's restore lock so the app can route users to the
  // "Restoring" page while a snapshot restore is in progress. Reads of the lock
  // doc are allowed by security rules even while writes are frozen.
  useEffect(() => {
    if (!companyName || !authReady) {
      setRestoreState({ company: companyName || null, userId: restoreUserId, status: 'unknown' });
      // Keep an observed freeze through a temporary sign-in failure. A
      // confirmed release after reauthentication must still reload the data.
      return undefined;
    }
    if (wasRestoringRef.current.company !== companyName
        || wasRestoringRef.current.userId !== restoreUserId) {
      wasRestoringRef.current = { company: companyName, userId: restoreUserId, active: false };
    }
    let disposed = false;
    let reloading = false;
    let expiryTimer = null;
    let readVersion = 0;
    const setRestoreStatus = (status) => setRestoreState({ company: companyName, userId: restoreUserId, status });
    const lockRef = doc(db, 'users', companyName, '_meta', 'restore_lock');
    setRestoreStatus('unknown');

    function fail(error) {
      if (disposed || reloading) return;
      readVersion += 1;
      clearTimeout(expiryTimer);
      console.error('restore_lock check failed', error);
      setRestoreStatus('error');
    }

    function applySnapshot(snapshot) {
      if (disposed || reloading || snapshot.metadata.fromCache) return;
      readVersion += 1;
      clearTimeout(expiryTimer);
      const data = snapshot.exists() ? snapshot.data() : null;
      const expiry = data?.expires_at;
      const expiresMs = typeof expiry?.toMillis === 'function' ? expiry.toMillis() : Infinity;
      const active = Boolean(
        data?.active === true && !['backup', 'restore_replay'].includes(data.operation)
        && (!Number.isFinite(expiresMs) || expiresMs > Date.now())
      );
      if (wasRestoringRef.current.active && !active) {
        // Admission after a freeze must load the restored data afresh. Keep
        // this page gated until the navigation completes.
        reloading = true;
        window.location.reload();
        return;
      }
      wasRestoringRef.current.active = active;
      setRestoreStatus(active ? 'active' : 'inactive');
      if (active && Number.isFinite(expiresMs)) {
        const delay = Math.max(0, Math.min(expiresMs - Date.now(), 2147483647));
        expiryTimer = setTimeout(verifyLock, delay);
      }
    }

    async function verifyLock() {
      if (disposed || reloading) return;
      const version = ++readVersion;
      try {
        const snapshot = await getDocFromServer(lockRef);
        if (!disposed && version === readVersion) applySnapshot(snapshot);
      } catch (error) {
        if (!disposed && version === readVersion) fail(error);
      }
    }

    const unsubscribe = onSnapshot(lockRef, { includeMetadataChanges: true }, applySnapshot, fail);
    const checkAfterSleep = () => {
      if (document.visibilityState === 'visible' && wasRestoringRef.current.active) verifyLock();
    };
    document.addEventListener('visibilitychange', checkAfterSleep);
    return () => {
      disposed = true;
      readVersion += 1;
      clearTimeout(expiryTimer);
      unsubscribe();
      document.removeEventListener('visibilitychange', checkAfterSleep);
    };
  }, [companyName, restoreUserId, authReady, restoreAttempt]);

  // Re-subscribe to the lock after a listener failure (the gate's Retry).
  const retryRestoreCheck = () => setRestoreAttempt((attempt) => attempt + 1);

  // Only an explicit retry may revisit a consumed documentation destination.
  async function retryDocsHandoff() {
    const user = mainAuth.currentUser;
    if (!docsHandoff || !user || signedOutRef.current || bridgePendingRef.current) return;
    const { returnPath } = docsHandoff;
    const generation = authGenerationRef.current;
    const retry = ++docsProbeRef.current;
    setDocsHandoff(null);
    try {
      const token = await user.getIdTokenResult(true);
      if (retry !== docsProbeRef.current || generation !== authGenerationRef.current
          || mainAuth.currentUser !== user) return;
      setDocsSessionCookie(token.token, token.expirationTime);
      await probeDocsHandoff(returnPath, user, generation);
    } catch (error) {
      console.error('Docs hand-off retry failed:', error);
      if (retry === docsProbeRef.current && generation === authGenerationRef.current
          && mainAuth.currentUser === user) {
        setDocsHandoff({ returnPath, status: 'unreachable' });
      }
    }
  }

  function dismissDocsHandoff() {
    docsProbeRef.current += 1;
    setDocsHandoff(null);
    forgetDocsReturnPath();
  }
  const restoreStatus = authReady && restoreState.company === companyName
    && restoreState.userId === restoreUserId ? restoreState.status : 'unknown';

  // Mirrors the company-root arm of firestore.rules: only god, or an admin of
  // this company, may update users/{companyId} — the document holding
  // company-wide settings such as agent_custom_instructions. For a non-god user
  // companyName IS their own organisation claim, so the role check alone is
  // equivalent to the rule here. Gate any org-level settings control on this so
  // members are shown a read-only view rather than a save that always fails.
  const isOrganisationAdmin = tokenRole === 'god' || tokenRole === 'admin';

  const value = {
    currentUser: currentUserPlatform,
    currentUserMain,
    currentUserPlatform,
    authInitialised,
    authReady,
    bridgeSigningIn,
    awaitingBridge,
    authError,
    companyName,
    restoreStatus,
    retryRestoreCheck,
    docsHandoff,
    retryDocsHandoff,
    dismissDocsHandoff,
    tokenOrganisation,
    tokenRole,
    isOrganisationAdmin,
    emulatedOrganisation,
    setEmulatedOrganisation,
    logout,
    db,
    platformAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
